import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Flame, X } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const MEAL_FILTERS = [["all", "All"], ["breakfast", "Breakfast"], ["lunch", "Lunch"], ["dinner", "Dinner"], ["snack", "Snacks"]];

export default function ClientRecipes() {
  const [recipes, setRecipes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [log, setLog] = useState({ date: today(), meal_type: "", servings: "1" });

  useEffect(() => { api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => {}); }, []);

  const shown = useMemo(
    () => (filter === "all" ? recipes : recipes.filter((r) => r.meal_type === filter)),
    [recipes, filter]
  );

  const openRecipe = (r) => {
    setActive(r);
    setLog({ date: today(), meal_type: r.meal_type, servings: "1" });
  };

  const logRecipe = async () => {
    try {
      await api.post(`/recipes/${active.recipe_id}/log`, {
        date: log.date, meal_type: log.meal_type, servings: parseFloat(log.servings) || 1,
      });
      toast.success(`${active.title} added to your diary`);
      setActive(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-recipes">
      <header>
        <div className="overline">Nourish with purpose</div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">Recipes</h1>
        <p className="text-secondary-luxe text-sm mt-3">Real food, beautifully done. Tap any recipe to log it straight to your diary.</p>
      </header>

      <div className="flex gap-2 flex-wrap">
        {MEAL_FILTERS.map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} data-testid={`recipe-filter-${key}`}
            className={filter === key ? "btn-primary text-xs" : "btn-secondary text-xs"}>{label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((r) => (
          <button key={r.recipe_id} onClick={() => openRecipe(r)}
            className="card-luxe text-left block hover:border-luxe-strong p-0 overflow-hidden"
            data-testid={`recipe-card-${r.recipe_id}`}>
            {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-48 object-cover" />}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <span className="overline">{r.meal_type}</span>
                {r.time_minutes && <span className="text-xs text-tertiary-luxe flex items-center gap-1"><Clock size={11} /> {r.time_minutes} min</span>}
              </div>
              <h3 className="font-serif text-2xl mt-2">{r.title}</h3>
              <div className="mt-3 text-xs text-secondary-luxe flex items-center gap-3">
                <span className="flex items-center gap-1"><Flame size={11} style={{ color: "var(--accent)" }} /> {Math.round(r.calories)} cal</span>
                <span>{Math.round(r.protein)}g protein</span>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {(r.tags || []).map((t) => (
                  <span key={t} className="font-label text-[9px] tracking-[0.2em] uppercase px-2 py-1"
                    style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{t}</span>
                ))}
              </div>
            </div>
          </button>
        ))}
        {shown.length === 0 && <div className="text-secondary-luxe">No recipes here yet.</div>}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6" style={{ background: "rgba(10,9,8,0.72)" }}
          onClick={() => setActive(null)} data-testid="recipe-modal">
          <div className="card-luxe max-w-2xl w-full max-h-[90vh] overflow-y-auto p-0" onClick={(e) => e.stopPropagation()}>
            {active.image_url && <img src={active.image_url} alt={active.title} className="w-full h-56 object-cover" />}
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{active.meal_type}{active.time_minutes ? ` · ${active.time_minutes} min` : ""}</div>
                  <h2 className="font-serif text-3xl mt-2">{active.title}</h2>
                </div>
                <button className="btn-ghost" onClick={() => setActive(null)} data-testid="close-recipe-modal"><X size={16} /></button>
              </div>
              {active.description && <p className="text-sm text-secondary-luxe mt-3">{active.description}</p>}

              <div className="grid grid-cols-5 gap-px mt-6" style={{ background: "var(--border-strong)" }}>
                {[["cal", Math.round(active.calories)], ["protein", `${Math.round(active.protein)}g`], ["carbs", `${Math.round(active.carbs)}g`], ["fat", `${Math.round(active.fats)}g`], ["fiber", `${Math.round(active.fiber)}g`]].map(([l, v]) => (
                  <div key={l} className="p-3 text-center" style={{ background: "var(--surface-2)" }}>
                    <div className="font-mono-luxe text-lg">{v}</div>
                    <div className="overline mt-1">{l}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <div className="overline">Ingredients</div>
                  <ul className="mt-3 space-y-2">
                    {(active.ingredients || []).map((ing, i) => (
                      <li key={`${ing.slice(0, 24)}-${i}`} className="text-sm text-secondary-luxe flex gap-2"><span style={{ color: "var(--accent)" }}>·</span> {ing}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="overline">Method</div>
                  <ol className="mt-3 space-y-3">
                    {(active.steps || []).map((s, i) => (
                      <li key={`${s.slice(0, 24)}-${i}`} className="text-sm text-secondary-luxe flex gap-3">
                        <span className="font-mono-luxe" style={{ color: "var(--accent)" }}>{i + 1}</span> {s}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="mt-7 pt-6 border-t border-luxe">
                <div className="overline">Log to my diary</div>
                <div className="flex gap-3 flex-wrap mt-3 items-center">
                  <input type="date" className="input-luxe max-w-[170px]" value={log.date}
                    onChange={(e) => setLog({ ...log, date: e.target.value })} data-testid="recipe-log-date" />
                  <select className="input-luxe max-w-[150px]" value={log.meal_type}
                    onChange={(e) => setLog({ ...log, meal_type: e.target.value })} data-testid="recipe-log-meal">
                    {["breakfast", "lunch", "dinner", "snack"].map((m) => <option key={m}>{m}</option>)}
                  </select>
                  <input className="input-luxe max-w-[100px]" value={log.servings} placeholder="Servings"
                    onChange={(e) => setLog({ ...log, servings: e.target.value })} data-testid="recipe-log-servings" />
                  <button className="btn-primary" onClick={logRecipe} data-testid="recipe-log-btn">Log this recipe</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
